import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useLocation } from 'react-router-dom';
import useStore from 'store/AccountStore';
import styles from './sidebar.module.css';
import usersIcon from '../../assets/icons/Users.png';
import op2miseLogo from '../../assets/icons/op2mise.svg';
import LogoCollapsed from '../../assets/icons/op2miseLogoSmall.svg';
import settingsIcon from '../../assets/icons/Settings-icon.svg';
import planningIcon from '../../assets/icons/Planning.svg';
import channelIcon from '../../assets/icons/Channel-icon.svg';
import BreakArchitectureIcon from '../../assets/icons/BreakArchitectureIcon.svg';
import ChannelConfigurationIcon from '../../assets/icons/channel-configuration.svg';
import ChannelArchitectureIcon from '../../assets/icons/channel-architecture.svg';
import scheduleIcon from '../../assets/icons/Schedule-icon.svg';
import titleGroupIcon from '../../assets/icons/TitleGroups.svg';
import organisationIcon from '../../assets/icons/Organisation-icon.svg';
import channelSidebarIcon from '../../assets/icons/channel-sidebar.svg';
import channelGroupSidebarIcon from '../../assets/icons/channelgroup-sidebar.svg';
import titleExclusionIcon from '../../assets/icons/Title-Exclusions.svg';
import notificationIcon from '../../assets/icons/Notification-Icon.svg';
import reportingIcon from '../../assets/icons/Reporting-icon.svg';
import emailTriggerIcon from '../../assets/icons/Email-Trigger.svg';
import chevRightIcon from '../../assets/icons/Chevron-Right.png';
import schedulesCreate from '../../assets/icons/Schedules-Generate-Create.svg';
import schedulesSubmit from '../../assets/icons/Schedules_Submit.svg';
import schedulesView from '../../assets/icons/Schedules_View.svg';
import {
  businessRulesIcon,
  businessRulesListIcon,
  commercialsIcon,
  contentTypesIcon,
  customBusinessRulesIcon,
  liveIcon,
  titleInclusionsIcon,
} from '../../assets';
import { AppModules } from 'enums';
import { SuperAdminModules } from 'enums';
import libraryIcon from '../../assets/icons/library.svg';
import libraryProgramsIcon from '../../assets/icons/library-programs.svg';
import { interstitials } from '../../assets';
import rightsIcon from '../../assets/icons/Rights_Management.svg';
import ChevronComponent from './ChevronComponent';
import {
  OriginalSchedulesMenu,
  NewSchedulesMenu,
} from 'constant/ScheduleMenuConstant';
import { useBusinessRule } from 'hooks/useBusinessRules';
import RouterService from 'core/router.service';

const { forwardRef, useImperativeHandle } = React;
const SidebarComponent = forwardRef(
  (
    {
      toggleSidebar,
      callbackUserLogout,
      isProfileUpdated,
      setIsProfileUpdated,
    },
    ref,
  ) => {
    const { user, setUser, isHydrated } = useStore((state) => state);
    const profileComponentRef = useRef();
    useImperativeHandle(ref, () => ({
      updateSidebarProfile(id) {
        profileComponentRef.current.updateSidebarProfile(id);
      },
    }));
    const location = useLocation();
    const [sidebarState, setSidebarState] = useState(false);
    const [settingsLinks, setSettingsLinks] = useState(false);
    const [businessRulesLinks, setBusinessRulesLinks] = useState(false);
    const [businessRulesV2Links, setBusinessRulesV2Links] = useState(false);
    const [planningLinks, setPlanningLinks] = useState(false);
    const [scheduleLinks, setScheduleLinks] = useState(false);
    const [channelSetupLinks, setChannelSetupLinks] = useState(false);
    const [profileLinkState, setProfileLinkState] = useState(false);
    const [libraryLinks, setLibraryLinks] = useState(false);
    const [reportingLinks, setReportingLinks] = useState(false);

    const { hasRule, getRuleValue } = useBusinessRule({
      scope: 'organisation',
      organisationId: user.organisationId,
    });
    const hasMonitoring = hasRule('Monitoring');
    const monitoringValue = getRuleValue('Monitoring');
    const canAccess = (path) => RouterService.isPageAccessible(path);
    const unifiedSchedulePath =
      user?.schedulesMenuType === NewSchedulesMenu
        ? '/advanced-unified-schedule'
        : '/new-unified-schedule';
    const showMonitoring =
      canAccess('/monitoring') &&
      hasMonitoring &&
      monitoringValue?.toLowerCase() === 'yes';
    const showChannelSetup =
      canAccess('/channel-profiles') ||
      canAccess('/architecture') ||
      canAccess('/break-patterns');
    const showLibrary =
      canAccess('/library/programs') ||
      canAccess('/library/interstitials') ||
      canAccess('/library/commercials') ||
      canAccess('/library/title-groups');
    const showDeals = canAccess('/rights');
    const showPlanning =
      canAccess('/inclusion') || canAccess('/title-exclusions');
    const showOriginalSchedules =
      user?.schedulesMenuType === OriginalSchedulesMenu;
    const showNewSchedules = user?.schedulesMenuType === NewSchedulesMenu;
    const showSchedules =
      (showOriginalSchedules &&
        (canAccess('/view-schedules') || canAccess('/schedule'))) ||
      (showNewSchedules &&
        (canAccess('/advanced-schedule') ||
          canAccess('/export') ||
          canAccess('/presentation-schedule') ||
          canAccess('/standby-schedule'))) ||
      canAccess('/submitted') ||
      canAccess(unifiedSchedulePath);
    const showReporting = canAccess('/notifications') || showMonitoring;
    const showBusinessRulesNav =
      canAccess('/business-rules') || canAccess('/custom-business-rules');
    const showBusinessRulesV2Nav =
      canAccess('/business-rule-definitions') ||
      canAccess('/business-rule-values');
    const showReportNav =
      canAccess('/report/configurations') || canAccess('/report/history');
    const showEmailTriggersNav = canAccess('/email-triggers');
    const showSuperAdminNav =
      canAccess('/channel-groups') ||
      canAccess('/channels') ||
      showBusinessRulesNav ||
      showBusinessRulesV2Nav ||
      showReportNav ||
      showEmailTriggersNav;
    const showSettingsUsers = canAccess('/users') && !showSuperAdminNav;
    const showSettingsOrganisation =
      canAccess('/organisation') && !showSuperAdminNav;
    const showOrgSettings =
      showSettingsUsers ||
      canAccess('/content-types') ||
      showSettingsOrganisation;
    const showOrgOperationalNav =
      showChannelSetup ||
      showLibrary ||
      showDeals ||
      showPlanning ||
      showSchedules;

    const toggleSidebarState = (state) => {
      setSidebarState(state);
      toggleSidebar(state);
    };

    const showBusinessRulesLinks = () => {
      setBusinessRulesLinks(!businessRulesLinks);
    };

    const showBusinessRulesV2Links = () => {
      setBusinessRulesV2Links(!businessRulesV2Links);
    };

    const showSettingsLinks = () => {
      setSettingsLinks(!settingsLinks);
    };

    const showScheduleLinks = () => {
      setScheduleLinks(!scheduleLinks);
    };

    const showPlanningLinks = () => {
      setPlanningLinks(!planningLinks);
    };

    const showChannelSetupLinks = () => {
      setChannelSetupLinks(!channelSetupLinks);
    };

    const showLibraryLinks = () => {
      setLibraryLinks(!libraryLinks);
    };

    const showReportingLinks = () => {
      setReportingLinks(!reportingLinks);
    };

    const hideProfileLink = (state) => {
      setProfileLinkState(state);
    };

    const getBrowserWidth = () =>
      Math.max(
        document.body.scrollWidth,
        document.documentElement.scrollWidth,
        document.body.offsetWidth,
        document.documentElement.offsetWidth,
        document.documentElement.clientWidth,
      );

    useEffect(() => {
      const handleResize = () => {
        if (getBrowserWidth() < 800) {
          toggleSidebarState(true);
        } else {
          toggleSidebarState(false);
        }
      };

      window.addEventListener('resize', handleResize);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    });

    useEffect(() => {
      if (getBrowserWidth() < 800) {
        toggleSidebarState(true);
      } else {
        toggleSidebarState(false);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getOrgAdminModules = (menu, item) => {
      const menuItem = AppModules[menu];
      const module = menuItem.subModules
        ? menuItem.subModules.find((module) => module.id === item)
        : menuItem;
      return module;
    };

    const getSuperAdminModule = (menu, item) => {
      const menuItem = SuperAdminModules[menu];
      const module = menuItem.subModules
        ? menuItem.subModules.find((module) => module.id === item)
        : menuItem;
      return module;
    };

    const handleMenuItemClick = ({ menu, item }) => {
      const module =
        user.roleId === 1
          ? getSuperAdminModule(menu, item)
          : getOrgAdminModules(menu, item);
      if (module) {
        const payload = {
          organization: user.organisationName,
          activeFunction: menu,
          menuItem: module.name,
        };
      }
    };

    return (
      <div className="position-relative vh-100">
        <div className={styles.sidebarHeader}>
          <img
            className={sidebarState ? styles.vaiLogoSmall : styles.vaiLogo}
            src={
              (user?.organisationBase64Logo ?? sidebarState)
                ? LogoCollapsed
                : op2miseLogo
            }
            alt="op2miseLogo"
          />
        </div>
        <div
          id="scrollbarStyle"
          className={`
          // sidebarState ? styles.sidebarStateListDiv : styles.sideBarListDiv,
          ${
            !profileLinkState
              ? styles.sidebarStateListDiv
              : styles.sideBarListDiv
          }  scrollbarStyle`}
        >
          <div className={styles.sidebarList}>
            {!isHydrated ? (
              <>
                <div className={styles.skeletonItem} />
                <div className={styles.skeletonItem} />
                <div className={styles.skeletonItem} />
                <div className={styles.skeletonItem} />
                <div className={styles.skeletonItem} />
                <div className={styles.skeletonItem} />
              </>
            ) : (
              <>
            {showOrgOperationalNav && (
              <>
                {/* CHANNEL SETUP */}
                {showChannelSetup && (
                  <>
                    <div
                      className={`${styles.vaiLink} nav-item nav-link px-0 py-2 position-relative`}
                      title={sidebarState ? 'Channel Setup' : null}
                      style={{ cursor: 'pointer' }}
                      onClick={showChannelSetupLinks}
                      onKeyDown={showChannelSetupLinks}
                      role="button"
                      tabIndex={0}
                    >
                      <div
                        style={{
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <img
                          className={sidebarState ? styles.vaiCenterText : null}
                          src={channelIcon}
                          alt="Channel Icon"
                          style={{ marginLeft: '10px' }}
                        />
                        <span
                          className={`${
                            sidebarState ? styles.hideText : null
                          } mx-3 `}
                        >
                          Channel Setup
                        </span>
                      </div>
                      <img
                        className={`${styles.vaiChevRight} ${
                          channelSetupLinks ? styles.vaiChevRightRotate : null
                        } ${sidebarState ? styles.hideText : null} mx-3 `}
                        src={chevRightIcon}
                        title={channelSetupLinks ? 'Show less' : 'Show more'}
                      />
                    </div>
                    <div
                      style={{
                        display: channelSetupLinks ? null : 'none',
                        marginLeft: sidebarState ? '2px' : '15px',
                      }}
                    >
                      <>
                        {/* CONFIGURATION */}
                        {canAccess('/channel-profiles') && (
                          <NavLink
                            title={sidebarState ? 'Configuration' : null}
                            className={(navData) =>
                              `${
                                navData.isActive
                                  ? styles.activeLink
                                  : styles.link
                              } nav-item nav-link px-0 py-2 position-relative`
                            }
                            onClick={() => {
                              handleMenuItemClick({
                                menu: 'CHANNEL_SETUP',
                                item: 'configuration',
                              });
                            }}
                            to={`${process.env.REACT_APP_SUBFOLDER}/channel-profiles`}
                          >
                            <img
                              className={sidebarState && styles.centerText}
                              style={{
                                marginLeft: sidebarState ? '15px' : '10px',
                                // marginBottom: '5px',
                              }}
                              src={ChannelConfigurationIcon}
                              alt="ChannelConfigurationIcon"
                            />
                            <span
                              className={` ${sidebarState && styles.hideText} mx-3`}
                            >
                              Configuration
                            </span>
                          </NavLink>
                        )}

                        {/* ARCHITECTURE */}
                        {canAccess('/architecture') && (
                          <NavLink
                            title={sidebarState ? 'Architecture' : null}
                            className={(navData) =>
                              `${
                                navData.isActive
                                  ? styles.activeLink
                                  : styles.link
                              } nav-item nav-link px-0 py-2 position-relative`
                            }
                            onClick={() => {
                              handleMenuItemClick({
                                menu: 'CHANNEL_SETUP',
                                item: 'architecture',
                              });
                            }}
                            to={`${process.env.REACT_APP_SUBFOLDER}/architecture`}
                          >
                            <img
                              className={sidebarState && styles.centerText}
                              style={{
                                marginLeft: sidebarState ? '14px' : '9px',
                                // marginBottom: '4px',
                              }}
                              src={ChannelArchitectureIcon}
                              alt="ChannelArchitectureIcon"
                            />
                            <span
                              className={` ${sidebarState && styles.hideText} mx-3`}
                            >
                              Architecture
                            </span>
                          </NavLink>
                        )}

                        {/* BREAK ARCHITECTURE */}
                        {canAccess('/break-patterns') && (
                          <NavLink
                            title={sidebarState ? 'Break Patterns' : null}
                            className={(navData) =>
                              `${
                                navData.isActive
                                  ? styles.activeLink
                                  : styles.link
                              } nav-item nav-link px-0 py-2 position-relative`
                            }
                            onClick={() => {
                              handleMenuItemClick({
                                menu: 'CHANNEL_SETUP',
                                item: 'break-patterns',
                              });
                            }}
                            to={`${process.env.REACT_APP_SUBFOLDER}/break-patterns`}
                          >
                            <img
                              className={sidebarState && styles.centerText}
                              style={{
                                marginLeft: sidebarState ? '14px' : '12px',
                                paddingRight: '1px',
                                // marginBottom: '2px',
                              }}
                              src={BreakArchitectureIcon}
                              alt="BreakArchitectureIcon"
                            />
                            <span
                              className={` ${sidebarState && styles.hideText} mx-3`}
                            >
                              Break Patterns
                            </span>
                          </NavLink>
                        )}
                      </>
                    </div>
                  </>
                )}
                {/* END CHANNEL SETUP */}

                {/* LIBRARY */}
                {showLibrary && (
                  <>
                    <div
                      className={`${styles.vaiLink} nav-item nav-link px-0 py-2 position-relative`}
                      title={sidebarState ? 'Library' : null}
                      style={{ cursor: 'pointer' }}
                      onClick={showLibraryLinks}
                      onKeyDown={showLibraryLinks}
                      role="button"
                      tabIndex={0}
                    >
                      <div
                        style={{
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <img
                          className={sidebarState ? styles.vaiCenterText : null}
                          src={libraryIcon}
                          alt="Library Icon"
                          style={{ marginLeft: '10px' }}
                        />
                        <span
                          className={`${
                            sidebarState ? styles.hideText : null
                          } mx-3 `}
                        >
                          Library
                        </span>
                      </div>
                      <img
                        className={`${styles.vaiChevRight} ${
                          libraryLinks ? styles.vaiChevRightRotate : null
                        } ${sidebarState ? styles.hideText : null} mx-3 `}
                        src={chevRightIcon}
                        title={libraryLinks ? 'Show less' : 'Show more'}
                      />
                    </div>
                    <div
                      style={{
                        display: libraryLinks ? null : 'none',
                        marginLeft: sidebarState ? '2px' : '15px',
                      }}
                    >
                      <>
                        {/* PROGRAMS */}
                        {canAccess('/library/programs') && (
                          <NavLink
                            title={sidebarState ? 'Programs' : null}
                            className={(navData) =>
                              `${
                                navData.isActive
                                  ? styles.activeLink
                                  : styles.link
                              } nav-item nav-link px-0 py-2 position-relative`
                            }
                            onClick={() => {
                              handleMenuItemClick({
                                menu: 'LIBRARY',
                                item: 'programs',
                              });
                            }}
                            to={`${process.env.REACT_APP_SUBFOLDER}/library/programs`}
                          >
                            <img
                              className={sidebarState && styles.centerText}
                              style={{
                                marginLeft: sidebarState ? '15px' : '10px',
                                // marginBottom: '5px',
                              }}
                              src={libraryProgramsIcon}
                              alt="Library Programs Icon"
                            />
                            <span
                              className={` ${sidebarState && styles.hideText} mx-3`}
                            >
                              Programs
                            </span>
                          </NavLink>
                        )}
                        {/* INTERSTITIAL */}
                        {canAccess('/library/interstitials') && (
                          <NavLink
                            title={sidebarState ? 'Interstitials' : null}
                            className={(navData) =>
                              `${
                                navData.isActive
                                  ? styles.activeLink
                                  : styles.link
                              } nav-item nav-link px-0 py-2 position-relative`
                            }
                            onClick={() => {
                              handleMenuItemClick({
                                menu: 'LIBRARY',
                                item: 'interstitials',
                              });
                            }}
                            to={`${process.env.REACT_APP_SUBFOLDER}/library/interstitials`}
                          >
                            <img
                              className={sidebarState && styles.centerText}
                              style={{
                                marginLeft: sidebarState ? '15px' : '10px',
                                // marginBottom: '5px',
                              }}
                              src={interstitials}
                              alt="Library Interstitials Icon"
                            />
                            <span
                              className={` ${sidebarState && styles.hideText} mx-3`}
                            >
                              Interstitials
                            </span>
                          </NavLink>
                        )}
                        {/* Commercials */}
                        {canAccess('/library/commercials') && (
                          <NavLink
                            title={sidebarState ? 'Commercials' : null}
                            className={(navData) =>
                              `${
                                navData.isActive
                                  ? styles.activeLink
                                  : styles.link
                              } nav-item nav-link px-0 py-2 position-relative`
                            }
                            onClick={() => {
                              handleMenuItemClick({
                                menu: 'LIBRARY',
                                item: 'commercials',
                              });
                            }}
                            to={`${process.env.REACT_APP_SUBFOLDER}/library/commercials`}
                          >
                            <img
                              className={sidebarState && styles.centerText}
                              style={{
                                marginLeft: sidebarState ? '15px' : '10px',
                                filter: 'invert(0.5)',
                                marginBottom: '5px',
                                height: '28px',
                              }}
                              src={commercialsIcon}
                              alt="Library Commercials Icon"
                            />
                            <span
                              className={` ${sidebarState && styles.hideText} mx-3`}
                            >
                              Commercials
                            </span>
                          </NavLink>
                        )}
                        {/* TITLE GROUPS */}
                        {canAccess('/library/title-groups') && (
                          <NavLink
                            title={sidebarState ? 'Title Groups' : null}
                            className={(navData) =>
                              `${
                                navData.isActive
                                  ? styles.activeLink
                                  : styles.link
                              } nav-item nav-link px-0 py-2 position-relative`
                            }
                            onClick={() => {
                              handleMenuItemClick({
                                menu: 'PLANNING',
                                item: 'title-groups',
                              });
                            }}
                            to={`${process.env.REACT_APP_SUBFOLDER}/library/title-groups`}
                          >
                            <img
                              className={sidebarState && styles.centerText}
                              style={{
                                marginLeft: sidebarState ? '15px' : '10px',
                                // marginBottom: '5px',
                              }}
                              src={titleGroupIcon}
                              alt="titlegroups"
                            />
                            <span
                              className={` ${sidebarState && styles.hideText} mx-3`}
                            >
                              Title Groups
                            </span>
                          </NavLink>
                        )}
                      </>
                    </div>
                  </>
                )}
                {/* END LIBRARY */}

                {/* RIGHTS */}
                {showDeals && (
                  <NavLink
                    className={(navData) =>
                      `${
                        navData.isActive ? styles.activeLink : styles.link
                      } nav-item nav-link px-0 py-2 position-relative`
                    }
                    title={sidebarState ? 'Deals' : null}
                    onClick={() => {
                      handleMenuItemClick({ menu: 'RIGHTS', item: 'rights' });
                    }}
                    to={`${process.env.REACT_APP_SUBFOLDER}/rights`}
                  >
                    <img
                      className={sidebarState && styles.centerText}
                      style={{ marginLeft: '10px', marginBottom: '5px' }}
                      src={rightsIcon}
                      alt="rights-icon"
                    />
                    <span
                      className={` ${sidebarState && styles.hideText} mx-3`}
                    >
                      Deals
                    </span>
                  </NavLink>
                )}
                {/* END RIGHTS */}

                {/* PLANNING */}
                {showPlanning && (
                  <>
                    <div
                      className={`${styles.vaiLink} nav-item nav-link px-0 py-2 position-relative`}
                      title={sidebarState ? 'Planning' : null}
                      style={{ cursor: 'pointer' }}
                      onClick={showPlanningLinks}
                      onKeyDown={showPlanningLinks}
                      role="button"
                      tabIndex={0}
                    >
                      <div
                        style={{
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <img
                          className={sidebarState ? styles.vaiCenterText : null}
                          src={planningIcon}
                          alt="PlanningIcon"
                          style={{ marginLeft: '10px' }}
                        />
                        <span
                          className={`${
                            sidebarState ? styles.hideText : null
                          } mx-3`}
                        >
                          Planning
                        </span>
                      </div>
                      <img
                        className={`${styles.vaiChevRight} ${
                          planningLinks ? styles.vaiChevRightRotate : null
                        } ${sidebarState ? styles.hideText : null} mx-3 `}
                        src={chevRightIcon}
                        title={planningLinks ? 'Show less' : 'Show more'}
                      />
                    </div>
                    <div
                      style={{
                        display: planningLinks ? null : 'none',
                        marginLeft: sidebarState ? '2px' : '15px',
                      }}
                    >
                      <>
                        {canAccess('/inclusion') && (
                          <NavLink
                            title={sidebarState ? 'Inclusions' : null}
                            className={(navData) =>
                              `${
                                navData.isActive
                                  ? styles.activeLink
                                  : styles.link
                              } nav-item nav-link px-0 py-2 position-relative`
                            }
                            onClick={() => {
                              handleMenuItemClick({
                                menu: 'PLANNING',
                                item: 'inclusions',
                              });
                            }}
                            to={`${process.env.REACT_APP_SUBFOLDER}/inclusion`}
                          >
                            <img
                              className={sidebarState && styles.centerText}
                              style={{
                                marginLeft: sidebarState ? '15px' : '10px',
                                marginBottom: '3px',
                                marginRight: '5px',
                              }}
                              src={titleInclusionsIcon}
                              alt="titleInclusionsIcon"
                            />
                            <span
                              className={` ${sidebarState && styles.hideText} mx-3`}
                            >
                              Inclusions
                            </span>
                          </NavLink>
                        )}

                        {canAccess('/title-exclusions') && (
                          <NavLink
                            title={sidebarState ? 'Exclusions' : null}
                            className={(navData) =>
                              `${
                                navData.isActive
                                  ? styles.activeLink
                                  : styles.link
                              } nav-item nav-link px-0 py-2 position-relative`
                            }
                            onClick={() => {
                              handleMenuItemClick({
                                menu: 'PLANNING',
                                item: 'exclusions',
                              });
                            }}
                            to={`${process.env.REACT_APP_SUBFOLDER}/title-exclusions`}
                          >
                            <img
                              className={sidebarState && styles.centerText}
                              style={{
                                marginLeft: sidebarState ? '15px' : '10px',
                                marginBottom: '5px',
                              }}
                              src={titleExclusionIcon}
                              alt="titleexclusionss"
                            />
                            <span
                              className={` ${sidebarState && styles.hideText} mx-3`}
                            >
                              Exclusions
                            </span>
                          </NavLink>
                        )}

                        {/* <NavLink
                        title={sidebarState ? 'Attributes Override' : null}
                        className={(navData) =>
                          `${
                            navData.isActive ? styles.activeLink : styles.link
                          } nav-item nav-link px-0 py-2 position-relative`
                        }
                        to={`${process.env.REACT_APP_SUBFOLDER}/attributes-override`}
                      >
                        <img
                          className={sidebarState && styles.centerText}
                          style={{
                            marginLeft: sidebarState ? '15px' : '10px',
                            marginBottom: '5px',
                          }}
                          src={attributesOverrideIcon}
                          alt="attributesOverride"
                        />
                        <span
                          className={` ${sidebarState && styles.hideText} mx-3`}
                        >
                          Attributes Override
                        </span>
                      </NavLink> */}
                      </>
                    </div>
                  </>
                )}

                {/** SCHEDULES */}
                {showSchedules && (
                  <>
                    <div
                      className={`${styles.vaiLink} nav-item nav-link px-0 py-2 position-relative`}
                      title={sidebarState ? 'Schedules' : null}
                      style={{ cursor: 'pointer' }}
                      onClick={showScheduleLinks}
                      onKeyDown={showScheduleLinks}
                      role="button"
                      tabIndex={0}
                    >
                      <div
                        style={{
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <img
                          className={sidebarState ? styles.vaiCenterText : null}
                          src={scheduleIcon}
                          alt="scheduleIcon"
                          style={{ marginLeft: '10px' }}
                        />
                        <span
                          className={`${
                            sidebarState ? styles.hideText : null
                          } mx-3`}
                        >
                          Schedules
                        </span>
                      </div>
                      <img
                        className={`${styles.vaiChevRight} ${
                          scheduleLinks ? styles.vaiChevRightRotate : null
                        } ${sidebarState ? styles.hideText : null} mx-3`}
                        src={chevRightIcon}
                        title={scheduleLinks ? 'Show less' : 'Show more'}
                      />
                    </div>
                    <div
                      style={{
                        display: scheduleLinks ? null : 'none',
                        marginLeft: sidebarState ? '2px' : '15px',
                      }}
                    >
                      {showOriginalSchedules && (
                        <>
                          {canAccess('/view-schedules') && (
                            <NavLink
                              title={sidebarState ? 'View' : null}
                              className={(navData) =>
                                `${
                                  navData.isActive
                                    ? styles.activeLink
                                    : styles.link
                                } nav-item nav-link px-0 py-2 position-relative  ${
                                  location.pathname ===
                                  `${process.env.REACT_APP_SUBFOLDER}/view-schedules`
                                    ? styles.activeLink
                                    : ''
                                }`
                              }
                              onClick={() => {
                                handleMenuItemClick({
                                  menu: 'SCHEDULES',
                                  item: 'view',
                                });
                              }}
                              to={`${process.env.REACT_APP_SUBFOLDER}/view-schedules`}
                            >
                              <img
                                className={sidebarState && styles.centerText}
                                style={{
                                  marginLeft: '15px',
                                  marginBottom: '5px',
                                }}
                                src={schedulesView}
                                alt="schedulesViewIcon"
                              />
                              <span
                                className={` ${sidebarState && styles.hideText} mx-3`}
                              >
                                View
                              </span>
                            </NavLink>
                          )}
                          {canAccess('/schedule') && (
                            <NavLink
                              title={sidebarState ? 'New' : null}
                              className={(navData) =>
                                `${
                                  navData.isActive
                                    ? styles.activeLink
                                    : styles.link
                                } nav-item nav-link px-0 py-2 position-relative  ${
                                  location.pathname ===
                                  `${process.env.REACT_APP_SUBFOLDER}/scheduler`
                                    ? styles.activeLink
                                    : ''
                                }`
                              }
                              onClick={() => {
                                handleMenuItemClick({
                                  menu: 'SCHEDULES',
                                  item: 'new',
                                });
                              }}
                              to={`${process.env.REACT_APP_SUBFOLDER}/schedule`}
                            >
                              <img
                                className={sidebarState && styles.centerText}
                                style={{
                                  marginLeft: '15px',
                                  marginBottom: '5px',
                                }}
                                src={schedulesCreate}
                                alt="schedulesCreate"
                              />
                              <span
                                className={` ${sidebarState && styles.hideText} mx-3`}
                              >
                                New
                              </span>
                            </NavLink>
                          )}
                        </>
                      )}
                      {showNewSchedules && (
                        <>
                          {canAccess('/advanced-schedule') && (
                            <NavLink
                              title={sidebarState ? 'Advanced' : null}
                              className={(navData) =>
                                `${
                                  navData.isActive
                                    ? styles.activeLink
                                    : styles.link
                                } nav-item nav-link px-0 py-2 position-relative  ${
                                  location.pathname ===
                                  `${process.env.REACT_APP_SUBFOLDER}/advanced-schedule`
                                    ? styles.activeLink
                                    : ''
                                }`
                              }
                              onClick={() => {
                                handleMenuItemClick({
                                  menu: 'SCHEDULES',
                                  item: 'advanced-schedule',
                                });
                              }}
                              to={`${process.env.REACT_APP_SUBFOLDER}/advanced-schedule`}
                            >
                              <img
                                className={sidebarState && styles.centerText}
                                style={{
                                  marginLeft: '15px',
                                  marginBottom: '5px',
                                }}
                                src={schedulesCreate}
                                alt="advancedIcon"
                              />
                              <span
                                className={` ${sidebarState && styles.hideText} mx-3`}
                              >
                                Advanced
                              </span>
                            </NavLink>
                          )}
                          {canAccess('/export') && (
                            <NavLink
                              title={sidebarState ? 'Export' : null}
                              className={(navData) =>
                                `${
                                  navData.isActive
                                    ? styles.activeLink
                                    : styles.link
                                } nav-item nav-link px-0 py-2 position-relative  ${
                                  location.pathname ===
                                  `${process.env.REACT_APP_SUBFOLDER}/export`
                                    ? styles.activeLink
                                    : ''
                                }`
                              }
                              onClick={() => {
                                handleMenuItemClick({
                                  menu: 'SCHEDULES',
                                  item: 'export',
                                });
                              }}
                              to={`${process.env.REACT_APP_SUBFOLDER}/export`}
                            >
                              <img
                                className={sidebarState && styles.centerText}
                                style={{
                                  marginLeft: '15px',
                                  marginBottom: '5px',
                                }}
                                src={schedulesSubmit}
                                alt="submissionIcon"
                              />
                              <span
                                className={` ${sidebarState && styles.hideText} mx-3`}
                              >
                                Export
                              </span>
                            </NavLink>
                          )}

                          {canAccess('/presentation-schedule') && (
                            <NavLink
                              title={sidebarState ? 'Presentation' : null}
                              className={(navData) =>
                                `${
                                  navData.isActive
                                    ? styles.activeLink
                                    : styles.link
                                } nav-item nav-link px-0 py-2 position-relative  ${
                                  location.pathname ===
                                  `${process.env.REACT_APP_SUBFOLDER}/presentation-schedule`
                                    ? styles.activeLink
                                    : ''
                                }`
                              }
                              onClick={() => {
                                handleMenuItemClick({
                                  menu: 'SCHEDULES',
                                  item: 'presentation-schedule',
                                });
                              }}
                              to={`${process.env.REACT_APP_SUBFOLDER}/presentation-schedule`}
                            >
                              <img
                                className={sidebarState && styles.centerText}
                                style={{
                                  marginLeft: '15px',
                                  marginBottom: '5px',
                                }}
                                src={schedulesSubmit}
                                alt="submissionIcon"
                              />
                              <span
                                className={` ${sidebarState && styles.hideText} mx-3`}
                              >
                                Presentation
                              </span>
                            </NavLink>
                          )}

                          {canAccess('/standby-schedule') && (
                            <NavLink
                              title={sidebarState ? 'Standby' : null}
                              className={(navData) =>
                                `${
                                  navData.isActive
                                    ? styles.activeLink
                                    : styles.link
                                } nav-item nav-link px-0 py-2 position-relative  ${
                                  location.pathname ===
                                  `${process.env.REACT_APP_SUBFOLDER}/standby-schedule`
                                    ? styles.activeLink
                                    : ''
                                }`
                              }
                              onClick={() => {
                                handleMenuItemClick({
                                  menu: 'SCHEDULES',
                                  item: 'standby-schedule',
                                });
                              }}
                              to={`${process.env.REACT_APP_SUBFOLDER}/standby-schedule`}
                            >
                              <img
                                className={sidebarState && styles.centerText}
                                style={{
                                  marginLeft: '15px',
                                  marginBottom: '5px',
                                }}
                                src={schedulesCreate}
                                alt="standbyIcon"
                              />
                              <span
                                className={` ${sidebarState && styles.hideText} mx-3`}
                              >
                                Standby
                              </span>
                            </NavLink>
                          )}
                        </>
                      )}
                      {canAccess('/submitted') && (
                        <NavLink
                          title={sidebarState ? 'Submitted' : null}
                          className={(navData) =>
                            `${
                              navData.isActive ? styles.activeLink : styles.link
                            } nav-item nav-link px-0 py-2 position-relative  ${
                              location.pathname ===
                              `${process.env.REACT_APP_SUBFOLDER}/submission-scheduler`
                                ? styles.activeLink
                                : ''
                            }`
                          }
                          onClick={() => {
                            handleMenuItemClick({
                              menu: 'SCHEDULES',
                              item: 'submission',
                            });
                          }}
                          to={`${process.env.REACT_APP_SUBFOLDER}/submitted`}
                        >
                          <img
                            className={sidebarState && styles.centerText}
                            style={{ marginLeft: '15px', marginBottom: '5px' }}
                            src={schedulesSubmit}
                            alt="submissionIcon"
                          />
                          <span
                            className={` ${sidebarState && styles.hideText} mx-3`}
                          >
                            Submitted
                          </span>
                        </NavLink>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
            {/** REPORTING */}
            {showReporting && (
              <>
                <div
                  title={sidebarState ? 'Reporting' : null}
                  className={`${styles.vaiLink} nav-item nav-link px-0 py-2 position-relative`}
                  style={{ cursor: 'pointer' }}
                  onClick={showReportingLinks}
                  onKeyDown={showReportingLinks}
                  role="button"
                  tabIndex={0}
                >
                  <div
                    style={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <img
                      className={sidebarState && styles.centerText}
                      src={reportingIcon}
                      alt="reportingIcon"
                      style={{ marginLeft: '10px' }}
                    />
                    <span
                      className={` ${
                        sidebarState ? styles.hideText : null
                      } mx-3`}
                    >
                      Reporting
                    </span>
                  </div>
                  <img
                    className={`${styles.vaiChevRight} ${
                      reportingLinks ? styles.vaiChevRightRotate : null
                    }  ${sidebarState ? styles.hideText : null} mx-3`}
                    src={chevRightIcon}
                    title={reportingLinks ? 'Show less' : 'Show more'}
                  />
                </div>
                <div
                  style={{
                    display: reportingLinks ? null : 'none',
                    marginLeft: sidebarState ? '2px' : '12px',
                  }}
                >
                  {canAccess('/notifications') && (
                    <NavLink
                      title={sidebarState ? 'Notifications' : null}
                      className={(navData) =>
                        `${
                          navData.isActive ? styles.activeLink : styles.link
                        } nav-item nav-link px-0 py-2 position-relative`
                      }
                      onClick={() => {
                        handleMenuItemClick({
                          menu: 'REPORTING',
                          item: 'notifications',
                        });
                      }}
                      to={`${process.env.REACT_APP_SUBFOLDER}/notifications`}
                    >
                      <img
                        className={sidebarState && styles.centerText}
                        style={{
                          marginLeft: sidebarState ? '15px' : '10px',
                          marginBottom: '5px',
                        }}
                        src={notificationIcon}
                        alt="notificationIcon"
                      />
                      <span
                        className={` ${sidebarState && styles.hideText} mx-3`}
                      >
                        Notifications
                      </span>
                    </NavLink>
                  )}

                  {showMonitoring && (
                    <NavLink
                      title={sidebarState ? 'Monitoring' : null}
                      className={(navData) =>
                        `${
                          navData.isActive ? styles.activeLink : styles.link
                        } nav-item nav-link px-0 py-2 position-relative`
                      }
                      onClick={() => {
                        handleMenuItemClick({
                          menu: 'REPORTING',
                          item: 'monitoring',
                        });
                      }}
                      to={`${process.env.REACT_APP_SUBFOLDER}/monitoring`}
                    >
                      <img
                        className={sidebarState && styles.centerText}
                        style={{
                          marginLeft: sidebarState ? '15px' : '10px',
                          marginBottom: '5px',
                        }}
                        src={liveIcon}
                        alt="monitoringIcon"
                      />
                      <span
                        className={` ${sidebarState && styles.hideText} mx-3`}
                      >
                        Monitoring
                      </span>
                    </NavLink>
                  )}
                </div>
              </>
            )}
            {/** END REPORTTING */}
            {showOrgSettings && (
              <>
                <div
                  title={sidebarState ? 'Settings' : null}
                  className={`${styles.vaiLink} nav-item nav-link px-0 py-2 position-relative`}
                  style={{ cursor: 'pointer' }}
                  onClick={showSettingsLinks}
                  onKeyDown={showSettingsLinks}
                  role="button"
                  tabIndex={0}
                >
                  <div
                    style={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <img
                      className={sidebarState ? styles.vaiCenterText : null}
                      src={settingsIcon}
                      alt="SettingsIcon"
                      style={{ marginLeft: '10px' }}
                    />
                    <span
                      className={` ${
                        sidebarState ? styles.hideText : null
                      } mx-3`}
                    >
                      Settings
                    </span>
                  </div>
                  <img
                    className={`${styles.vaiChevRight} ${
                      settingsLinks ? styles.vaiChevRightRotate : null
                    }  ${sidebarState ? styles.hideText : null} mx-3`}
                    src={chevRightIcon}
                    title={settingsLinks ? 'Show less' : 'Show more'}
                  />
                </div>
                <div
                  style={{
                    display: settingsLinks ? null : 'none',
                    marginLeft: sidebarState ? '2px' : '12px',
                  }}
                >
                  {showSettingsUsers && (
                    <NavLink
                      title={sidebarState ? 'Users' : null}
                      className={(navData) =>
                        `${
                          navData.isActive ? styles.activeLink : styles.link
                        } nav-item nav-link px-0 py-2 position-relative`
                      }
                      onClick={() => {
                        handleMenuItemClick({
                          menu: 'SETTINGS',
                          item: 'users',
                        });
                      }}
                      to={`${process.env.REACT_APP_SUBFOLDER}/users`}
                    >
                      <img
                        className={sidebarState && styles.centerText}
                        style={{
                          marginLeft: sidebarState ? '15px' : '10px',
                          marginBottom: '5px',
                        }}
                        src={usersIcon}
                        alt="UsersIcon"
                      />
                      <span
                        className={` ${sidebarState && styles.hideText} mx-3`}
                      >
                        Users
                      </span>
                    </NavLink>
                  )}

                  {canAccess('/content-types') && (
                    <NavLink
                      title={sidebarState ? 'Content Types' : null}
                      className={(navData) =>
                        `${
                          navData.isActive ? styles.activeLink : styles.link
                        } nav-item nav-link px-0 py-2 position-relative`
                      }
                      onClick={() => {
                        handleMenuItemClick({
                          menu: 'SETTINGS',
                          item: 'content-types',
                        });
                      }}
                      to={`${process.env.REACT_APP_SUBFOLDER}/content-types`}
                    >
                      <img
                        className={sidebarState && styles.centerText}
                        style={{
                          marginLeft: sidebarState ? '15px' : '10px',
                          marginBottom: '5px',
                          height: '21px',
                        }}
                        src={contentTypesIcon}
                        alt="ContentTypes"
                      />
                      <span
                        className={` ${sidebarState && styles.hideText} mx-3`}
                      >
                        Content Types
                      </span>
                    </NavLink>
                  )}
                  {showSettingsOrganisation && (
                    <NavLink
                      title={sidebarState ? 'Organisations' : null}
                      className={(navData) =>
                        `${
                          navData.isActive ? styles.activeLink : styles.link
                        } nav-item nav-link px-0 py-2 position-relative`
                      }
                      onClick={() => {
                        handleMenuItemClick({
                          menu: 'SETTINGS',
                          item: 'organisation',
                        });
                      }}
                      to={`${process.env.REACT_APP_SUBFOLDER}/organisation`}
                    >
                      <img
                        className={sidebarState && styles.centerText}
                        style={{
                          marginLeft: sidebarState ? '15px' : '10px',
                          marginBottom: '5px',
                          height: '21px',
                        }}
                        src={organisationIcon}
                        alt="organisationIcon"
                      />
                      <span
                        className={` ${sidebarState && styles.hideText} mx-3`}
                      >
                        Organisations
                      </span>
                    </NavLink>
                  )}

                  {/* {roleID !== 2 && (
                    <>
                      <NavLink
                        title={sidebarState ? 'Channel Groups' : null}
                        className={(navData) =>
                          `${
                            navData.isActive ? styles.activeLink : styles.link
                          } nav-item nav-link px-0 py-2 position-relative`
                        }
                        to={`${process.env.REACT_APP_SUBFOLDER}/channel-groups`}
                      >
                        <img
                          className={sidebarState && styles.centerText}
                          style={{
                            marginLeft: sidebarState ? '15px' : '10px',
                            marginBottom: '5px',
                          }}
                          src={channelGroupSidebarIcon}
                          alt="channelGroupIcon"
                        />
                        <span
                          className={` ${sidebarState && styles.hideText} mx-3`}
                        >
                          Channel Groups
                        </span>
                      </NavLink>

                      <NavLink
                        title={sidebarState ? 'Channels' : null}
                        className={(navData) =>
                          `${
                            navData.isActive ? styles.activeLink : styles.link
                          } nav-item nav-link px-0 py-2 position-relative`
                        }
                        to={`${process.env.REACT_APP_SUBFOLDER}/channels`}
                      >
                        <img
                          className={sidebarState && styles.centerText}
                          style={{
                            marginLeft: sidebarState ? '15px' : '10px',
                            marginBottom: '5px',
                          }}
                          src={channelSidebarIcon}
                          alt="channelIcon"
                        />
                        <span
                          className={` ${sidebarState && styles.hideText} mx-3`}
                        >
                          Channels
                        </span>
                      </NavLink>
                    </>
                  )} */}
                </div>
              </>
            )}

            {showSuperAdminNav && (
              <>
                {canAccess('/organisation') && (
                  <NavLink
                    title={sidebarState ? 'Organisations' : null}
                    className={(navData) =>
                      `${
                        navData.isActive ? styles.activeLink : styles.link
                      } nav-item nav-link px-0 py-2 position-relative`
                    }
                    onClick={() => {
                      handleMenuItemClick({ menu: 'ORGANISATIONS' });
                    }}
                    to={`${process.env.REACT_APP_SUBFOLDER}/organisation`}
                  >
                    <img
                      className={sidebarState && styles.centerText}
                      style={{ marginLeft: '10px', marginBottom: '5px' }}
                      src={organisationIcon}
                      alt="organisationIcon"
                    />
                    <span
                      className={` ${sidebarState && styles.hideText} mx-3`}
                    >
                      Organisations
                    </span>
                  </NavLink>
                )}

                {canAccess('/users') && (
                  <NavLink
                    title={sidebarState ? 'Users' : null}
                    className={(navData) =>
                      `${
                        navData.isActive ? styles.activeLink : styles.link
                      } nav-item nav-link px-0 py-2 position-relative`
                    }
                    onClick={() => {
                      handleMenuItemClick({ menu: 'USERS' });
                    }}
                    to={`${process.env.REACT_APP_SUBFOLDER}/users`}
                  >
                    <img
                      className={sidebarState && styles.centerText}
                      style={{ marginLeft: '10px', marginBottom: '5px' }}
                      src={usersIcon}
                      alt="usersIcon"
                    />
                    <span
                      className={` ${sidebarState && styles.hideText} mx-3`}
                    >
                      Users
                    </span>
                  </NavLink>
                )}

                {canAccess('/channel-groups') && (
                  <NavLink
                    title={sidebarState ? 'Channel Groups' : null}
                    className={(navData) =>
                      `${
                        navData.isActive ? styles.activeLink : styles.link
                      } nav-item nav-link px-0 py-2 position-relative`
                    }
                    onClick={() => {
                      handleMenuItemClick({ menu: 'CHANNEL_GROUPS' });
                    }}
                    to={`${process.env.REACT_APP_SUBFOLDER}/channel-groups`}
                  >
                    <img
                      className={sidebarState && styles.centerText}
                      style={{ marginLeft: '5px', marginBottom: '5px' }}
                      src={channelGroupSidebarIcon}
                      alt="channelGroupIcon"
                    />
                    <span
                      className={` ${sidebarState && styles.hideText} mx-3`}
                    >
                      Channel Groups
                    </span>
                  </NavLink>
                )}

                {canAccess('/channels') && (
                  <NavLink
                    title={sidebarState ? 'Channels' : null}
                    className={(navData) =>
                      `${
                        navData.isActive ? styles.activeLink : styles.link
                      } nav-item nav-link px-0 py-2 position-relative`
                    }
                    onClick={() => {
                      handleMenuItemClick({ menu: 'CHANNELS' });
                    }}
                    to={`${process.env.REACT_APP_SUBFOLDER}/channels`}
                  >
                    <img
                      className={sidebarState && styles.centerText}
                      style={{ marginLeft: '10px', marginBottom: '5px' }}
                      src={channelSidebarIcon}
                      alt="channelIcon"
                    />
                    <span
                      className={` ${sidebarState && styles.hideText} mx-3`}
                    >
                      Channels
                    </span>
                  </NavLink>
                )}

                {/* Business Rules */}
                {showBusinessRulesNav && (
                  <>
                    <div
                      title={sidebarState ? 'Business Rules' : null}
                      className={`${styles.vaiLink} nav-item nav-link px-0 py-2 position-relative`}
                      style={{ cursor: 'pointer' }}
                      onClick={showBusinessRulesLinks}
                      onKeyDown={showBusinessRulesLinks}
                      role="button"
                      tabIndex={0}
                    >
                      <div
                        style={{
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <img
                          className={sidebarState ? styles.vaiCenterText : null}
                          src={businessRulesIcon}
                          alt="business rules icon"
                          style={{ marginLeft: '10px' }}
                        />
                        <span
                          className={`${
                            sidebarState ? styles.hideText : null
                          } mx-3`}
                        >
                          Business Rules
                        </span>
                      </div>
                      <img
                        className={`${styles.vaiChevRight} ${
                          businessRulesLinks ? styles.vaiChevRightRotate : null
                        }  ${sidebarState ? styles.hideText : null} mx-3`}
                        src={chevRightIcon}
                        title={businessRulesLinks ? 'Show less' : 'Show more'}
                        alt=""
                      />
                    </div>

                    <div
                      style={{
                        display: businessRulesLinks ? null : 'none',
                        marginLeft: sidebarState ? '2px' : '12px',
                      }}
                    >
                      {/* List */}
                      {canAccess('/business-rules') && (
                        <NavLink
                          title={sidebarState ? 'List' : null}
                          className={(navData) =>
                            `${
                              navData.isActive ? styles.activeLink : styles.link
                            } nav-item nav-link px-0 py-2 position-relative`
                          }
                          onClick={() => {
                            handleMenuItemClick({
                              menu: 'BUSINESS_RULES',
                              item: 'business-rules',
                            });
                          }}
                          to={`${process.env.REACT_APP_SUBFOLDER}/business-rules`}
                        >
                          <img
                            className={sidebarState && styles.centerText}
                            style={{
                              marginLeft: sidebarState ? '15px' : '10px',
                              marginBottom: '5px',
                            }}
                            src={businessRulesListIcon}
                            alt="business rules list icon"
                          />
                          <span
                            className={` ${sidebarState && styles.hideText} mx-3`}
                          >
                            List
                          </span>
                        </NavLink>
                      )}

                      {/* List */}
                      {canAccess('/custom-business-rules') && (
                        <NavLink
                          title={sidebarState ? 'List' : null}
                          className={(navData) =>
                            `${
                              navData.isActive ? styles.activeLink : styles.link
                            } nav-item nav-link px-0 py-2 position-relative`
                          }
                          onClick={() => {
                            handleMenuItemClick({
                              menu: 'BUSINESS_RULES',
                              item: 'custom-business-rules',
                            });
                          }}
                          to={`${process.env.REACT_APP_SUBFOLDER}/custom-business-rules`}
                        >
                          <img
                            className={sidebarState && styles.centerText}
                            style={{
                              marginLeft: sidebarState ? '15px' : '10px',
                              marginBottom: '5px',
                            }}
                            src={customBusinessRulesIcon}
                            alt="custom business rules icon"
                          />
                          <span
                            className={` ${sidebarState && styles.hideText} mx-3`}
                          >
                            Values
                          </span>
                        </NavLink>
                      )}
                    </div>
                  </>
                )}

                {/* Business Rule v2 */}
                {showBusinessRulesV2Nav && (
                  <>
                    <div
                      title={sidebarState ? 'Business Rule v2' : null}
                      className={`${styles.vaiLink} nav-item nav-link px-0 py-2 position-relative`}
                      style={{ cursor: 'pointer' }}
                      onClick={showBusinessRulesV2Links}
                      onKeyDown={showBusinessRulesV2Links}
                      role="button"
                      tabIndex={0}
                    >
                      <div
                        style={{
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <img
                          className={sidebarState ? styles.vaiCenterText : null}
                          src={businessRulesIcon}
                          alt="business rule v2 icon"
                          style={{ marginLeft: '10px' }}
                        />
                        <span
                          className={`${
                            sidebarState ? styles.hideText : null
                          } mx-3`}
                        >
                          Business Rule v2
                        </span>
                      </div>
                      <img
                        className={`${styles.vaiChevRight} ${
                          businessRulesV2Links
                            ? styles.vaiChevRightRotate
                            : null
                        }  ${sidebarState ? styles.hideText : null} mx-3`}
                        src={chevRightIcon}
                        title={businessRulesV2Links ? 'Show less' : 'Show more'}
                        alt=""
                      />
                    </div>

                    <div
                      style={{
                        display: businessRulesV2Links ? null : 'none',
                        marginLeft: sidebarState ? '2px' : '12px',
                      }}
                    >
                      {canAccess('/business-rule-definitions') && (
                        <NavLink
                          title={sidebarState ? 'Definitions' : null}
                          className={(navData) =>
                            `${
                              navData.isActive ? styles.activeLink : styles.link
                            } nav-item nav-link px-0 py-2 position-relative`
                          }
                          onClick={() => {
                            handleMenuItemClick({
                              menu: 'BUSINESS_RULES_V2',
                              item: 'definitions',
                            });
                          }}
                          to={`${process.env.REACT_APP_SUBFOLDER}/business-rule-definitions`}
                        >
                          <img
                            className={sidebarState && styles.centerText}
                            style={{
                              marginLeft: sidebarState ? '15px' : '10px',
                              marginBottom: '5px',
                            }}
                            src={businessRulesListIcon}
                            alt="business rule definitions icon"
                          />
                          <span
                            className={` ${sidebarState && styles.hideText} mx-3`}
                          >
                            Definitions
                          </span>
                        </NavLink>
                      )}

                      {canAccess('/business-rule-values') && (
                        <NavLink
                          title={sidebarState ? 'Values' : null}
                          className={(navData) =>
                            `${
                              navData.isActive ? styles.activeLink : styles.link
                            } nav-item nav-link px-0 py-2 position-relative`
                          }
                          onClick={() => {
                            handleMenuItemClick({
                              menu: 'BUSINESS_RULES_V2',
                              item: 'values',
                            });
                          }}
                          to={`${process.env.REACT_APP_SUBFOLDER}/business-rule-values`}
                        >
                          <img
                            className={sidebarState && styles.centerText}
                            style={{
                              marginLeft: sidebarState ? '15px' : '10px',
                              marginBottom: '5px',
                            }}
                            src={customBusinessRulesIcon}
                            alt="business rule values icon"
                          />
                          <span
                            className={` ${sidebarState && styles.hideText} mx-3`}
                          >
                            Values
                          </span>
                        </NavLink>
                      )}
                    </div>
                  </>
                )}

                {showEmailTriggersNav && (
                  <>
                    <div
                      title={sidebarState ? 'Settings' : null}
                      className={`${styles.vaiLink} nav-item nav-link px-0 py-2 position-relative`}
                      style={{ cursor: 'pointer' }}
                      onClick={showSettingsLinks}
                      onKeyDown={showSettingsLinks}
                      role="button"
                      tabIndex={0}
                    >
                      <div
                        style={{
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <img
                          className={sidebarState ? styles.vaiCenterText : null}
                          src={settingsIcon}
                          alt="SettingsIcon"
                          style={{ marginLeft: '10px' }}
                        />
                        <span
                          className={`${
                            sidebarState ? styles.hideText : null
                          } mx-3`}
                        >
                          Settings
                        </span>
                      </div>
                      <img
                        className={`${styles.vaiChevRight} ${
                          settingsLinks ? styles.vaiChevRightRotate : null
                        }  ${sidebarState ? styles.hideText : null} mx-3`}
                        src={chevRightIcon}
                        title={settingsLinks ? 'Show less' : 'Show more'}
                      />
                    </div>
                    <div
                      style={{
                        display: settingsLinks ? null : 'none',
                        marginLeft: sidebarState ? '2px' : '12px',
                      }}
                    >
                      <NavLink
                        title={sidebarState ? 'Email Triggers' : null}
                        className={(navData) =>
                          `${
                            navData.isActive ? styles.activeLink : styles.link
                          } nav-item nav-link px-0 py-2 position-relative`
                        }
                        onClick={() => {
                          handleMenuItemClick({
                            menu: 'SETTINGS',
                            item: 'email-triggers',
                          });
                        }}
                        to={`${process.env.REACT_APP_SUBFOLDER}/email-triggers`}
                      >
                        <img
                          className={sidebarState && styles.centerText}
                          style={{
                            marginLeft: sidebarState ? '15px' : '10px',
                            marginBottom: '5px',
                          }}
                          src={emailTriggerIcon}
                          alt="emailTriggerIcon"
                        />
                        <span
                          className={` ${sidebarState && styles.hideText} mx-3`}
                        >
                          Email Triggers
                        </span>
                      </NavLink>
                    </div>
                  </>
                )}
              </>
            )}
              </>
            )}
          </div>
        </div>

        <div>
          <ChevronComponent toggleSidebar={toggleSidebarState} />
        </div>
      </div>
    );
  },
);
SidebarComponent.propTypes = {
  toggleSidebar: PropTypes.func.isRequired,
  callbackUserLogout: PropTypes.func.isRequired,
};
SidebarComponent.displayName = 'SidebarComponent';
export default SidebarComponent;
